import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  AppConfig,
  ClientFormDetail,
  ClientFormStatus,
  ClientFormSummary,
  FileRef,
  FormDefinition,
  Template,
  TemplateSummary,
  User,
} from '../lib/types';

export const queryKeys = {
  me: ['me'] as const,
  config: ['config'] as const,
  templates: ['templates'] as const,
  template: (id: string) => ['templates', id] as const,
  clientForms: ['client-forms'] as const,
  clientFormList: (filters: object) => ['client-forms', 'list', filters] as const,
  clientForm: (id: string) => ['client-forms', id] as const,
};

// ---------- Auth ----------

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      try {
        return (await api.get<{ user: User }>('/auth/me')).user;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ user: User }>('/auth/login', input),
    onSuccess: ({ user }) => qc.setQueryData(queryKeys.me, user),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(queryKeys.me, null);
    },
  });
}

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config,
    queryFn: () => api.get<AppConfig>('/config'),
    staleTime: Infinity,
  });
}

// ---------- Uploads ----------

export function uploadTemplateImage(file: File) {
  return api.upload<{ file: FileRef }>('/files', file).then((r) => r.file);
}

// ---------- Templates ----------

export function useTemplates() {
  return useQuery({
    queryKey: queryKeys.templates,
    queryFn: () => api.get<{ templates: TemplateSummary[] }>('/templates').then((r) => r.templates),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: queryKeys.template(id),
    queryFn: () => api.get<{ template: Template }>(`/templates/${id}`).then((r) => r.template),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FormDefinition) =>
      api.post<{ template: Template }>('/templates', input).then((r) => r.template),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.templates }),
  });
}

export function useSaveTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FormDefinition) =>
      api.put<{ template: Template }>(`/templates/${id}`, input).then((r) => r.template),
    onSuccess: (template) => {
      qc.setQueryData(queryKeys.template(id), template);
      void qc.invalidateQueries({ queryKey: queryKeys.templates, exact: true });
    },
  });
}

export function useDuplicateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ template: Template }>(`/templates/${id}/duplicate`).then((r) => r.template),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.templates }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ archived: boolean }>(`/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.templates }),
  });
}

// ---------- Client links ----------

export interface ClientFormFilters {
  status?: ClientFormStatus;
  q?: string;
  templateId?: string;
}

export function useClientForms(filters: ClientFormFilters) {
  return useQuery({
    queryKey: queryKeys.clientFormList(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, String(v));
      const qs = params.toString();
      return api
        .get<{ clientForms: ClientFormSummary[] }>(`/client-forms${qs ? `?${qs}` : ''}`)
        .then((r) => r.clientForms);
    },
    placeholderData: (prev) => prev,
  });
}

export function useClientForm(id: string) {
  return useQuery({
    queryKey: queryKeys.clientForm(id),
    queryFn: () =>
      api.get<{ clientForm: ClientFormDetail }>(`/client-forms/${id}`).then((r) => r.clientForm),
  });
}

export function useCreateClientForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { templateId: string; clientName: string; clientEmail?: string }) =>
      api.post<{ clientForm: ClientFormDetail }>('/client-forms', input).then((r) => r.clientForm),
    onSuccess: (cf) => {
      qc.setQueryData(queryKeys.clientForm(cf.id), cf);
      void qc.invalidateQueries({ queryKey: queryKeys.clientForms });
      void qc.invalidateQueries({ queryKey: queryKeys.templates });
    },
  });
}

/** Generic action on one client link (update, reopen, sync, send) that returns the new detail. */
export function useClientFormAction<TInput>(
  id: string,
  action: (input: TInput) => Promise<{ clientForm: ClientFormDetail }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => action(input).then((r) => r.clientForm),
    onSuccess: (cf) => {
      qc.setQueryData(queryKeys.clientForm(id), cf);
      void qc.invalidateQueries({ queryKey: queryKeys.clientForms, exact: false });
    },
  });
}

export function useDeleteClientForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/client-forms/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.clientForms });
      void qc.invalidateQueries({ queryKey: queryKeys.templates });
    },
  });
}
